import React, { useState, useEffect } from "react";
import {
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  IconButton,
  Chip,
  Divider,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Code as JsonIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import type { Permission } from "@/types/auth";

/**
 * Editor avanzado para estructura JSON de permisos
 *
 * REGLAS APLICADAS:
 * - Solo <Stack> para layout (NO Grid)
 * - Componentes con size="small"
 * - dense={true} en listas
 * - Máximo 500 líneas
 */

interface PermissionStructureEditorProps {
  open: boolean;
  permission: Permission | null;
  onClose: () => void;
  onSave: (
    permissionId: string,
    estructura: Record<string, any>,
  ) => Promise<void>;
}

interface RouteStructure {
  [menu: string]: {
    [funcion: string]: boolean;
  };
}

interface PermissionStructure {
  [ruta: string]: {
    menus: RouteStructure;
  };
}

export function PermissionStructureEditor({
  open,
  permission,
  onClose,
  onSave,
}: PermissionStructureEditorProps) {
  const [estructura, setEstructura] = useState<PermissionStructure>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState("");

  // Cargar estructura del permiso
  useEffect(() => {
    if (permission?.estructura_permisos) {
      const estructuraFormateada = formatearEstructura(
        permission.estructura_permisos,
      );
      setEstructura(estructuraFormateada || {});
      setJsonText(JSON.stringify(permission.estructura_permisos, null, 2));
    } else {
      setEstructura({});
      setJsonText("{}");
    }
    setError(null);
  }, [permission, open]);

  const formatearEstructura = (estructura: any): PermissionStructure => {
    if (!estructura || typeof estructura !== "object") {
      return {};
    }

    // Asegurar que cada ruta tenga la estructura correcta
    const estructuraFormateada: PermissionStructure = {};

    Object.entries(estructura).forEach(([ruta, rutaData]: [string, any]) => {
      if (rutaData && typeof rutaData === "object") {
        estructuraFormateada[ruta] = {
          menus:
            rutaData.menus && typeof rutaData.menus === "object"
              ? rutaData.menus
              : {},
        };
      }
    });

    return estructuraFormateada;
  };

  const funcionesDefecto = [
    "crear",
    "editar",
    "eliminar",
    "ver",
    "listar",
    "activar",
    "desactivar",
    "asignar",
    "revocar",
  ];

  const rutasDefecto = [
    "/usuarios",
    "/grupos",
    "/permisos",
    "/dashboard",
    "/reportes",
    "/configuracion",
  ];

  const menusDefecto: Record<string, string[]> = {
    "/usuarios": ["usuarios", "perfiles"],
    "/grupos": ["grupos", "roles"],
    "/permisos": ["permisos", "accesos"],
    "/dashboard": ["tablero", "estadisticas"],
    "/reportes": ["reportes", "analytics"],
    "/configuracion": ["configuracion", "sistema"],
  };

  const agregarRuta = (nuevaRuta: string) => {
    if (!nuevaRuta.trim() || estructura[nuevaRuta]) return;

    const ruta = nuevaRuta.startsWith("/") ? nuevaRuta : `/${nuevaRuta}`;
    const menusRuta = menusDefecto[ruta] || ["principal"];

    const nuevaEstructura = { ...estructura };
    nuevaEstructura[ruta] = {
      menus: {},
    };

    menusRuta.forEach((menu) => {
      if (nuevaEstructura[ruta]) {
        nuevaEstructura[ruta].menus[menu] = {};
        funcionesDefecto.forEach((funcion) => {
          if (nuevaEstructura[ruta]?.menus[menu]) {
            nuevaEstructura[ruta].menus[menu][funcion] = false;
          }
        });
      }
    });

    setEstructura(nuevaEstructura);
  };

  const eliminarRuta = (ruta: string) => {
    const nuevaEstructura = { ...estructura };
    delete nuevaEstructura[ruta];
    setEstructura(nuevaEstructura);
  };

  const _agregarMenu = (ruta: string, nuevoMenu: string) => {
    if (
      !nuevoMenu.trim() ||
      !estructura[ruta] ||
      estructura[ruta]?.menus[nuevoMenu]
    )
      return;

    const nuevaEstructura = { ...estructura };
    if (nuevaEstructura[ruta]) {
      nuevaEstructura[ruta].menus[nuevoMenu] = {};
      funcionesDefecto.forEach((funcion) => {
        if (nuevaEstructura[ruta]?.menus[nuevoMenu]) {
          nuevaEstructura[ruta].menus[nuevoMenu][funcion] = false;
        }
      });
    }
    setEstructura(nuevaEstructura);
  };

  const eliminarMenu = (ruta: string, menu: string) => {
    const nuevaEstructura = { ...estructura };
    if (nuevaEstructura[ruta]?.menus[menu]) {
      delete nuevaEstructura[ruta].menus[menu];
    }
    setEstructura(nuevaEstructura);
  };

  const toggleFuncion = (ruta: string, menu: string, funcion: string) => {
    const nuevaEstructura = { ...estructura };
    if (nuevaEstructura[ruta]?.menus[menu]?.[funcion] !== undefined) {
      nuevaEstructura[ruta].menus[menu][funcion] =
        !nuevaEstructura[ruta].menus[menu][funcion];
    }
    setEstructura(nuevaEstructura);
  };

  const toggleTodoMenu = (ruta: string, menu: string) => {
    const nuevaEstructura = { ...estructura };
    const menuData = nuevaEstructura[ruta]?.menus[menu];
    if (!menuData) return;

    const todasActivas = Object.values(menuData).every(Boolean);

    Object.keys(menuData || {}).forEach((funcion) => {
      if (nuevaEstructura[ruta]?.menus[menu]) {
        nuevaEstructura[ruta].menus[menu][funcion] = !todasActivas;
      }
    });
    setEstructura(nuevaEstructura);
  };

  const cargarTemplate = (template: "full" | "readonly" | "empty") => {
    let nuevaEstructura: PermissionStructure = {};

    switch (template) {
      case "full":
        rutasDefecto.forEach((ruta) => {
          nuevaEstructura[ruta] = { menus: {} };
          const menus = menusDefecto[ruta] || ["principal"];
          menus.forEach((menu) => {
            if (nuevaEstructura[ruta]) {
              nuevaEstructura[ruta].menus[menu] = {};
              funcionesDefecto.forEach((funcion) => {
                if (nuevaEstructura[ruta]?.menus[menu]) {
                  nuevaEstructura[ruta].menus[menu][funcion] = true;
                }
              });
            }
          });
        });
        break;
      case "readonly":
        rutasDefecto.forEach((ruta) => {
          nuevaEstructura[ruta] = { menus: {} };
          const menus = menusDefecto[ruta] || ["principal"];
          menus.forEach((menu) => {
            if (nuevaEstructura[ruta]) {
              nuevaEstructura[ruta].menus[menu] = {};
              funcionesDefecto.forEach((funcion) => {
                if (nuevaEstructura[ruta]?.menus[menu]) {
                  nuevaEstructura[ruta].menus[menu][funcion] = [
                    "ver",
                    "listar",
                  ].includes(funcion);
                }
              });
            }
          });
        });
        break;
      case "empty":
        nuevaEstructura = {};
        break;
    }

    setEstructura(nuevaEstructura);
  };

  const handleSaveJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      const estructuraFormateada = formatearEstructura(parsed);
      setEstructura(estructuraFormateada);
      setJsonMode(false);
      setError(null);
    } catch {
      setError("JSON inválido. Por favor, revisa la sintaxis.");
    }
  };

  const handleSave = async () => {
    if (!permission) return;

    try {
      setLoading(true);
      setError(null);
      await onSave(permission.id, estructura);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error guardando estructura");
    } finally {
      setLoading(false);
    }
  };

  const contarPermisos = () => {
    let total = 0;
    let activos = 0;

    if (estructura && typeof estructura === "object") {
      Object.values(estructura).forEach((ruta) => {
        if (ruta && ruta.menus && typeof ruta.menus === "object") {
          Object.values(ruta.menus).forEach((menu) => {
            if (menu && typeof menu === "object") {
              Object.values(menu).forEach((activo) => {
                total++;
                if (activo) activos++;
              });
            }
          });
        }
      });
    }

    return { total, activos };
  };

  const { total, activos } = contarPermisos();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row">
          <Box>
            <Typography variant="h6">
              Editor de Estructura de Permisos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {permission?.nombre} - {activos}/{total} permisos activos
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<JsonIcon />}
              onClick={() => {
                setJsonMode(!jsonMode);
                if (!jsonMode) {
                  setJsonText(JSON.stringify(estructura, null, 2));
                }
              }}
            >
              {jsonMode ? "Visual" : "JSON"}
            </Button>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {jsonMode ? (
            <Stack spacing={2}>
              <TextField
                label="Estructura JSON"
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                multiline
                rows={20}
                fullWidth
                size="small"
              />
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  onClick={handleSaveJson}
                  startIcon={<SaveIcon />}
                >
                  Aplicar JSON
                </Button>
                <Button
                  size="small"
                  onClick={() =>
                    setJsonText(JSON.stringify(estructura, null, 2))
                  }
                >
                  Recargar
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2}>
              {/* Templates rápidos */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Templates Rápidos
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => cargarTemplate("full")}>
                    Acceso Total
                  </Button>
                  <Button
                    size="small"
                    onClick={() => cargarTemplate("readonly")}
                  >
                    Solo Lectura
                  </Button>
                  <Button size="small" onClick={() => cargarTemplate("empty")}>
                    Vacío
                  </Button>
                </Stack>
              </Box>

              <Divider />

              {/* Agregar nueva ruta */}
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Nueva Ruta"
                  placeholder="/nueva-ruta"
                  size="small"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      agregarRuta((e.target as HTMLInputElement).value);
                      (e.target as HTMLInputElement).value = "";
                    }
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  Presiona Enter para agregar
                </Typography>
              </Stack>

              {/* Lista de rutas */}
              {Object.keys(estructura).length === 0 ? (
                <Alert severity="info">
                  No hay rutas configuradas. Usa los templates o agrega rutas
                  manualmente.
                </Alert>
              ) : (
                Object.entries(estructura).map(([ruta, rutaData]) => (
                  <Accordion key={ruta}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ width: "100%" }}
                      >
                        <Typography variant="subtitle2">{ruta}</Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        <Chip
                          size="small"
                          label={`${Object.keys(rutaData?.menus || {}).length} menús`}
                        />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        {/* Controles de la ruta */}
                        <Stack direction="row" justifyContent="flex-end">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => eliminarRuta(ruta)}
                            title="Eliminar ruta"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>

                        {Object.entries(rutaData?.menus || {}).map(
                          ([menu, menuData]) => (
                            <Box key={menu}>
                              <Stack direction="row" spacing={2}>
                                <Typography variant="body1">
                                  Carpeta {menu}
                                </Typography>
                                <Button
                                  size="small"
                                  onClick={() => toggleTodoMenu(ruta, menu)}
                                >
                                  Toggle All
                                </Button>
                                <IconButton
                                  size="small"
                                  onClick={() => eliminarMenu(ruta, menu)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Stack>
                              <Stack direction="row" spacing={1}>
                                {Object.entries(menuData || {}).map(
                                  ([funcion, activo]) => (
                                    <FormControlLabel
                                      key={funcion}
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={Boolean(activo)}
                                          onChange={() =>
                                            toggleFuncion(ruta, menu, funcion)
                                          }
                                        />
                                      }
                                      label={funcion}
                                    />
                                  ),
                                )}
                              </Stack>
                            </Box>
                          ),
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading} size="small">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          size="small"
          startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
        >
          {loading ? "Guardando..." : "Guardar Estructura"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
